import React, { FC, useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { useLocation, useMatch } from 'react-router-dom';

import useMediaQuery from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyRewards } from 'state/loyalty/hooks/use-loyalty-rewards';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useLoyaltyUserTier } from 'state/loyalty/hooks/use-loyalty-user-tier';
import { routes } from 'utils/routing';

import { appHeaderSelector } from './constants';
import AuthenticatedBanner from './loyalty-banner-authenticated';
import UnauthenticatedBanner from './loyalty-banner-unauthenticated';
import LoyaltyStickyBanner from './loyalty-sticky-banner';
import AuthenticatedTiersBanner from './loyalty-tiers-banner-authenticated';
import UnauthenticatedTiersBanner from './loyalty-tiers-banner-unauthenticated';
import { BannerContainer, EmptySpacer } from './styled';
import { loyaltyBannerObserverOptions } from './utils';

const LoyaltyBanner: FC = () => {
  const isHome = useMatch(routes.base);
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { loyaltyUser, loading: loyaltyLoading } = useLoyaltyUser();
  const { loyaltyTierKey: userTier } = useLoyaltyUserTier();
  const { loading: authLoading } = useAuthContext();
  const { enableLoyaltyTiers } = useLoyaltyRewards(loyaltyUser);
  const [showStickyBanner, setShowStickyBanner] = useState(false);
  const loyaltyBannerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('mobile');
  const location = useLocation();
  const { formatMessage } = useIntl();

  const showLoyaltyBannerV2 = useFlag(LaunchDarklyFlag.SHOW_LOYALTY_BANNER_V2);
  const showLoyaltyBannerV2InHome = useFlag(LaunchDarklyFlag.SHOW_LOYALTY_BANNER_V2_IN_HOME);

  // This flag will be removed once the feature goes live and configuration will only be controlled from Sanity
  const enableLoyaltyTiersFlag = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_TIERS);

  // The routes that should never display the carousel
  const hideCarouselRoutes = [
    routes.rewardsList,
    formatMessage({ id: 'routes.loyaltyDashboard' }),
    formatMessage({ id: 'routes.loyaltyHome' }),
  ];

  useEffect(() => {
    const bannerElement = loyaltyBannerRef.current;

    if (!isMobile) {
      return;
    }

    const observer = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
      setShowStickyBanner(!entries[0].isIntersecting);
    }, loyaltyBannerObserverOptions);

    if (bannerElement) {
      observer.observe(bannerElement);
    }

    return () => {
      if (bannerElement) {
        observer.unobserve(bannerElement);
      }
    };
  }, [isMobile, userTier]);

  const loading = loyaltyLoading || authLoading;
  const TierBanner = loyaltyUser ? (
    <AuthenticatedTiersBanner hideCarouselRoutes={hideCarouselRoutes} />
  ) : (
    <UnauthenticatedTiersBanner />
  );
  const portalRoot = document.querySelector(appHeaderSelector);

  if (enableLoyaltyTiersFlag && enableLoyaltyTiers && loyaltyEnabled) {
    return (
      <BannerContainer data-testid="loyalty-tiers-banner" ref={loyaltyBannerRef}>
        {isMobile &&
          portalRoot &&
          createPortal(<LoyaltyStickyBanner visible={showStickyBanner} />, portalRoot)}
        {!showStickyBanner ? (
          TierBanner
        ) : (
          <EmptySpacer
            hasCarousel={hideCarouselRoutes.every(route => location.pathname.indexOf(route) === -1)}
          />
        )}
      </BannerContainer>
    );
  }

  if (!loyaltyEnabled || loading || !showLoyaltyBannerV2) {
    return null;
  }

  // Hide the loyalty banner v2 if:
  // - The user is authenticated
  // - The user is on the "home" page
  // - The LD flag "show-loyalty-banner-v2-in-home" is false
  if (loyaltyUser && isHome && !showLoyaltyBannerV2InHome) {
    return null;
  }

  return (
    <BannerContainer data-testid="loyalty-banner">
      {loyaltyUser ? <AuthenticatedBanner /> : <UnauthenticatedBanner />}
    </BannerContainer>
  );
};

export default LoyaltyBanner;
